import { Link } from "react-router-dom";
import { Gift, ArrowRight } from "lucide-react";
import { useSite } from "../context/SiteContent";
import { mediaUrl } from "../api";
import { BookingButton } from "./Booking";
import { Eyebrow, SectionHeading, LinkArrow, ServiceCard, PromoCard, HoursTable, ContactList, Img } from "./parts";

function Cta({ cta, primary, testid }) {
  if (!cta?.visible || !cta?.label) return null;
  const cls = primary
    ? "bg-[var(--brand-accent)] text-[var(--brand-primary-dark)] hover:bg-[var(--brand-accent-hover)]"
    : "border border-stone-50/40 text-stone-50 hover:bg-white/10";
  const inner = <span className="inline-flex items-center gap-2">{cta.label}<ArrowRight className="h-4 w-4" /></span>;
  const base = `inline-flex px-6 py-3 rounded-full text-sm font-semibold tracking-wide transition-colors ${cls}`;
  return cta.url?.startsWith("http") ? <a href={cta.url} data-testid={testid} className={base}>{inner}</a> : <Link to={cta.url || "/"} data-testid={testid} className={base}>{inner}</Link>;
}

function Hero({ hp, business }) {
  const hero = mediaUrl(hp.hero_media_id);
  return (
    <section className="relative bg-[var(--brand-primary)] text-stone-50 overflow-hidden" data-testid="home-hero">
      {hero && <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <div className={`absolute inset-0 ${hero ? "bg-gradient-to-r from-[var(--brand-primary-dark-90)] via-[var(--brand-primary-70)] to-[var(--brand-primary-30)]" : "bg-[radial-gradient(circle_at_80%_20%,var(--brand-accent-20),transparent_50%)]"}`} />
      <div className="relative max-w-6xl mx-auto px-5 py-24 sm:py-32 lg:py-40 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <Eyebrow>{business?.tagline || "Willkommen"}</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mt-4" data-testid="home-title">{hp.title}</h1>
          {hp.subtitle && <p className="font-serif italic text-xl sm:text-2xl text-[var(--brand-accent-light)] mt-4" data-testid="home-subtitle">{hp.subtitle}</p>}
          {hp.intro && <p className="text-base text-stone-200/90 mt-6 max-w-xl leading-relaxed" data-testid="home-intro">{hp.intro}</p>}
          <div className="flex flex-wrap gap-3 mt-9">
            <BookingButton testid="home-booking-button" className="!px-6 !py-3" />
            <Cta cta={hp.cta_primary} primary={false} testid="home-cta-primary" />
            <Cta cta={hp.cta_secondary} testid="home-cta-secondary" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { content } = useSite();
  const hp = content.homepage || {};
  const b = content.business || {};
  const sections = [...(hp.sections || [])].sort((a, c) => a.order - c.order).filter((s) => s.visible).map((s) => s.key);
  const show = (k) => sections.includes(k);

  const blocks = {
    services: content.featured_services?.length > 0 && (
      <section key="services" className="max-w-6xl mx-auto px-5 py-16 sm:py-24" data-testid="home-services">
        <SectionHeading eyebrow="Unsere Leistungen" title="Ausgewählte Behandlungen" action={<LinkArrow to="/leistungen" testid="home-services-link">Alle Leistungen</LinkArrow>} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{content.featured_services.map((s) => <ServiceCard key={s.id} s={s} compact />)}</div>
      </section>
    ),
    promotions: content.featured_promotions?.length > 0 && (
      <section key="promotions" className="bg-[var(--brand-primary)] py-16 sm:py-24" data-testid="home-promotions">
        <div className="max-w-6xl mx-auto px-5">
          <SectionHeading eyebrow="Aktuell" title="Unsere Angebote" light action={<LinkArrow to="/angebote" light testid="home-promos-link">Alle Angebote</LinkArrow>} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{content.featured_promotions.map((p) => <PromoCard key={p.id} p={p} dark />)}</div>
        </div>
      </section>
    ),
    gallery: content.gallery_preview?.length > 0 && (
      <section key="gallery" className="max-w-6xl mx-auto px-5 py-16 sm:py-24" data-testid="home-gallery">
        <SectionHeading eyebrow="Einblicke" title="Aus unserem Salon" action={<LinkArrow to="/galerie" testid="home-gallery-link">Zur Galerie</LinkArrow>} />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {content.gallery_preview.map((g, i) => <Img key={g.id} id={g.media_id} alt={g.title} className={`w-full rounded-xl ${i === 0 ? "col-span-2 row-span-2 h-full min-h-[260px]" : "h-40 sm:h-52"}`} />)}
        </div>
      </section>
    ),
    image_sections: hp.image_sections?.length > 0 && (
      <section key="image_sections" className="max-w-6xl mx-auto px-5 py-10 space-y-16" data-testid="home-image-sections">
        {hp.image_sections.map((sec, i) => (
          <div key={i} className={`grid md:grid-cols-12 gap-8 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
            <Img id={sec.media_id} alt={sec.title} className="md:col-span-7 w-full h-72 sm:h-96 rounded-2xl" />
            <div className="md:col-span-5">
              <h3 className="font-serif text-3xl text-stone-900">{sec.title}</h3>
              <p className="text-base text-stone-600 mt-3 leading-relaxed whitespace-pre-line">{sec.text}</p>
            </div>
          </div>
        ))}
      </section>
    ),
    hours: (
      <section key="hours" className="max-w-6xl mx-auto px-5 py-16 sm:py-24" data-testid="home-hours">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5 rounded-2xl bg-white border border-stone-200/70 p-7 shadow-sm">
            <Eyebrow>Öffnungszeiten</Eyebrow>
            <h2 className="font-serif text-3xl text-stone-900 mt-2 mb-5">Wann Sie uns erreichen</h2>
            <HoursTable hours={content.hours} special={content.special_hours} />
          </div>
          <div className="md:col-span-7 rounded-2xl bg-[#F1EAE0] p-7">
            <Eyebrow>Besuchen Sie uns</Eyebrow>
            <h2 className="font-serif text-3xl text-stone-900 mt-2 mb-5">{b.name}</h2>
            <ContactList b={b} />
            {b.maps_url && <a href={b.maps_url} target="_blank" rel="noreferrer" data-testid="home-maps-link" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[var(--brand-primary)]">Route planen <ArrowRight className="h-4 w-4" /></a>}
          </div>
        </div>
      </section>
    ),
    contact: (
      <section key="contact" className="max-w-6xl mx-auto px-5 pb-10" data-testid="home-loyalty">
        <div className="rounded-3xl bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-primary-dark)] text-stone-50 p-8 sm:p-12 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <Eyebrow>Treueprogramm</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl mt-2">Jeder Besuch wird belohnt.</h2>
            <p className="text-stone-300 mt-3 max-w-lg">Sammeln Sie Punkte, sichern Sie sich Prämien und behalten Sie Ihr Guthaben im Kundenportal immer im Blick.</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/portal" data-testid="home-portal-button" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--brand-accent)] text-[var(--brand-primary-dark)] text-sm font-semibold hover:bg-[var(--brand-accent-hover)] transition-colors"><Gift className="h-4 w-4" /> Zum Kundenportal</Link>
          </div>
        </div>
      </section>
    ),
  };

  return (
    <>
      <Hero hp={hp} business={b} />
      {sections.filter(show).map((k) => blocks[k] || null)}
    </>
  );
}
